import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Category = 'single' | 'double' | 'villa' | 'winter';

interface GalleryItem {
  img: string;
  category: Category;
}

const ITEMS: GalleryItem[] = [
  {
    category: 'single',
    img: 'https://readdy.ai/api/search-image?query=Premium%20single%20car%20enclosed%20garage%20of%20a%20luxury%20villa%20with%20one%20luxury%20car%20inside%2C%20anthracite%20dark%20facade%20with%20warm%20wood%20slats%20and%20gold%20accent%20lighting%2C%20dusk%20atmosphere%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-single-01&orientation=landscape',
  },
  {
    category: 'double',
    img: 'https://readdy.ai/api/search-image?query=Spacious%20premium%20double%20car%20enclosed%20garage%20of%20a%20luxury%20modern%20villa%20with%20two%20luxury%20cars%20inside%2C%20anthracite%20dark%20facade%20with%20natural%20wood%20details%20and%20gold%20accent%20lighting%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-double-01&orientation=landscape',
  },
  {
    category: 'villa',
    img: 'https://readdy.ai/api/search-image?query=Luxury%20villa%20residential%20development%20with%20multiple%20integrated%20premium%20enclosed%20garage%20systems%2C%20modern%20architecture%2C%20anthracite%20dark%20facades%20with%20warm%20wood%20and%20gold%20accent%20lighting%2C%20dusk%20atmosphere%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-villa-01&orientation=landscape',
  },
  {
    category: 'winter',
    img: 'https://readdy.ai/api/search-image?query=Elegant%20glass%20winter%20garden%20attached%20to%20a%20luxury%20villa%20with%20lush%20green%20plants%20inside%2C%20anthracite%20dark%20structure%20with%20natural%20wood%20and%20warm%20gold%20lighting%2C%20dusk%20atmosphere%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-winter-01&orientation=landscape',
  },
  {
    category: 'single',
    img: 'https://readdy.ai/api/search-image?query=Modern%20single%20car%20premium%20garage%20with%20a%20luxury%20sedan%20inside%20and%20dramatic%20warm%20gold%20accent%20lighting%20on%20an%20anthracite%20facade%20with%20wood%20cladding%2C%20night%20ambient%20atmosphere%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-single-02&orientation=landscape',
  },
  {
    category: 'double',
    img: 'https://readdy.ai/api/search-image?query=Double%20car%20premium%20garage%20facade%20with%20two%20open%20doors%20revealing%20luxury%20cars%2C%20anthracite%20dark%20cladding%20with%20wood%20slats%20and%20warm%20gold%20lighting%20strips%2C%20dusk%20ambient%20atmosphere%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-double-02&orientation=landscape',
  },
  {
    category: 'villa',
    img: 'https://readdy.ai/api/search-image?query=Premium%20villa%20project%20with%20integrated%20garage%20system%20and%20modern%20minimalist%20architecture%2C%20anthracite%20facade%20with%20natural%20wood%20accents%20and%20warm%20gold%20lighting%2C%20landscaped%20surroundings%2C%20dusk%20atmosphere%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-villa-02&orientation=landscape',
  },
  {
    category: 'winter',
    img: 'https://readdy.ai/api/search-image?query=Premium%20garage%20combined%20with%20an%20elegant%20winter%20garden%20glass%20extension%20of%20a%20luxury%20villa%2C%20anthracite%20dark%20facade%20with%20natural%20wood%20and%20warm%20gold%20accent%20lighting%2C%20dusk%20ambient%20atmosphere%2C%20minimalist%20luxury%20aesthetic%2C%20high%20detail%2C%20professional%20editorial%20photography&width=1200&height=800&seq=vg-gal-winter-02&orientation=landscape',
  },
];

const FILTER_LABELS: Record<'all' | Category, string> = {
  all: 's9.filterAll',
  single: 's9.filterSingle',
  double: 's9.filterDouble',
  villa: 's9.filterVilla',
  winter: 's9.filterWinter',
};

export default function GallerySection() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | Category>('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filtered = filter === 'all' ? ITEMS : ITEMS.filter((i) => i.category === filter);

  const close = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  }, [filtered.length]);
  const next = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % filtered.length));
  }, [filtered.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, close, prev, next]);

  return (
    <section id="projeler" className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-3xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
            {t('s9.title')}
          </h2>
        </div>

        <div className="reveal mt-10 flex flex-wrap items-center justify-center gap-2">
          {(Object.keys(FILTER_LABELS) as Array<'all' | Category>).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors cursor-pointer ${
                filter === key
                  ? 'bg-primary-500 text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {t(FILTER_LABELS[key])}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((item, index) => (
            <button
              key={item.img}
              type="button"
              onClick={() => setLightboxIndex(index)}
              className="reveal img-hover-zoom group overflow-hidden rounded-lg border border-background-200 bg-background-50 text-start cursor-pointer"
            >
              <div className="relative h-52 w-full overflow-hidden md:h-60">
                <img
                  src={item.img}
                  alt={t(FILTER_LABELS[item.category])}
                  title={t(FILTER_LABELS[item.category])}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-foreground-950/60 to-transparent p-4 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="flex items-center gap-2 text-sm text-background-50">
                    <i className="ri-zoom-in-line" aria-hidden="true"></i>
                    {t(FILTER_LABELS[item.category])}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && filtered[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/95 p-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-5 end-5 flex h-11 w-11 items-center justify-center rounded-full bg-foreground-800/70 text-background-50 transition-colors hover:bg-primary-500 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute start-3 md:start-6 flex h-11 w-11 items-center justify-center rounded-full bg-foreground-800/70 text-background-50 transition-colors hover:bg-primary-500 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl" aria-hidden="true"></i>
          </button>

          <img
            src={filtered[lightboxIndex].img}
            alt={t(FILTER_LABELS[filtered[lightboxIndex].category])}
            className="max-h-[85vh] max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute end-3 md:end-6 flex h-11 w-11 items-center justify-center rounded-full bg-foreground-800/70 text-background-50 transition-colors hover:bg-primary-500 hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
          </button>
        </div>
      )}
    </section>
  );
}